import { useOnboarding } from "@/contexts/OnboardingContext";
import { useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  CheckCircle2, Users, Contact, Package, Bot, Beaker,
  Globe, BarChart3, MessageSquare, Calendar, Target, X, Lightbulb,
} from "lucide-react";

const ICON_MAP: Record<string, React.ComponentType<{ className?: string }>> = {
  CheckCircle2, Users, Contact, Package, Bot, Beaker,
  Globe, BarChart3, MessageSquare, Calendar, Target,
};

const TIP_INSTRUCTIONS: Record<string, string> = {
  members: 'Role até a seção "Membros do Workspace" e clique em "Convidar membro". Adicione o e-mail e a função (admin ou atendente) de cada pessoa do seu time.',
  leads: 'Clique no botão "+" para adicionar um contato manualmente, ou use "Importar CSV" para trazer sua base toda de uma vez. Preencha nome e telefone (com DDD) para começar.',
  products: 'Clique em "Novo Produto" para cadastrar seus itens. Adicione nome, descrição detalhada e preço. Quanto mais informação, melhor a IA vende!',
  agent: 'Clique em "Novo Agente" para criar seu assistente. Defina o nome, o tom de voz e as instruções de comportamento. Depois vincule os produtos que ele pode oferecer.',
  agent_business: 'Abra seu agente e vá até a aba "Negócio". Preencha o campo "Sobre a Empresa" com informações como o que você vende, seus diferenciais e horários de funcionamento.',
  agent_faq: 'Na aba "Negócio" do agente, role até a seção "Perguntas Frequentes". Clique em "Adicionar pergunta" e cadastre as dúvidas mais comuns dos seus clientes com as respostas ideais.',
  agent_behavior: 'Na aba "Comportamento" do agente, configure o idioma, o tom de voz e defina o que a IA pode ou não pode fazer durante o atendimento.',
  test_agent: "Escolha o agente que você criou e simule uma conversa como se fosse um cliente. Teste perguntas sobre preços, horários e produtos para ver como a IA responde.",
  integrations: 'Conecte seus canais de atendimento. Clique em "Configurar" no WhatsApp ou Instagram para integrar. Após conectar, a IA começa a responder automaticamente!',
  dashboard: "Este é seu painel de controle. Aqui você acompanha mensagens recebidas, tempo de resposta, desempenho da IA e métricas do seu atendimento.",
  conversations: "Aqui ficam todas as conversas em tempo real. Você pode alternar entre WhatsApp e Instagram, ativar/pausar a IA por conversa e enviar mensagens manuais.",
  appointments: "Aqui você vê as reuniões agendadas pela IA ou manualmente. Conecte seu Google Agenda nas integrações para sincronizar tudo automaticamente.",
  crm: "Agora explore o funil de vendas! Arraste os contatos entre as etapas (Lead, Qualificado, Proposta, Fechado) para acompanhar o progresso de cada oportunidade.",
};

const STEP_ROUTES: Record<string, string> = {
  members: "/settings",
  leads: "/leads",
  products: "/products",
  agent: "/agents",
  agent_business: "/agents",
  agent_faq: "/agents",
  agent_behavior: "/agents",
  test_agent: "/agents-test",
  integrations: "/integrations",
  dashboard: "/dashboard",
  conversations: "/conversations",
  appointments: "/appointments",
  crm: "/leads",
};

export function OnboardingTip() {
  const { steps, activeStepSlug, shownTips, dismissTip, isOnboardingActive } = useOnboarding();
  const location = useLocation();

  if (!isOnboardingActive || !activeStepSlug) return null;
  if (shownTips.includes(activeStepSlug)) return null;

  const expectedRoute = STEP_ROUTES[activeStepSlug];
  if (!expectedRoute || location.pathname !== expectedRoute) return null;

  const instruction = TIP_INSTRUCTIONS[activeStepSlug];
  if (!instruction) return null;

  const step = steps.find((s) => s.slug === activeStepSlug);
  const IconComp = step ? ICON_MAP[step.icon] || Lightbulb : Lightbulb;

  return (
    <div className="mx-4 mt-3 mb-1 animate-in slide-in-from-top-2 fade-in duration-300">
      <div className="relative rounded-xl border border-violet-500/30 bg-card p-4 shadow-lg">
        <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-violet-500/5 to-purple-600/5 pointer-events-none" />
        <div className="relative flex gap-3">
          <div className="flex-shrink-0 h-9 w-9 rounded-full bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
            <IconComp className="h-4 w-4 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <h4 className="text-sm font-semibold text-foreground mb-1">
              {step?.title || "Dica"}
            </h4>
            <p className="text-sm text-muted-foreground leading-relaxed">
              {instruction}
            </p>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={dismissTip}
            className="flex-shrink-0 h-8 px-3 text-xs font-medium"
          >
            Entendi
          </Button>
          <button
            onClick={dismissTip}
            className="absolute top-2 right-2 p-1 rounded-md text-muted-foreground/50 hover:text-muted-foreground transition-colors"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}
